import cn from "classnames";
import * as React from "react";

import AddIcon from "./icons/Add.svg";
import AppearanceIcon from "./icons/Theme.svg";
import ArrowLeftIcon from "./icons/ArrowLeft.svg";
import ArrowRightIcon from "./icons/ArrowRight.svg";
import BookmarkSelectedIcon from "./icons/BookmarkSelected.svg";
import BookmarkUnselectedIcon from "./icons/BookmarkUnselected.svg";
import CaretDownIcon from "./icons/ChevronDown.svg";
import CaretLeftIcon from "./icons/ChevronLeft.svg";
import CaretRightIcon from "./icons/ChevronRight.svg";
import CaretUpIcon from "./icons/ChevronUp.svg";
import CheckmarkIcon from "./icons/Checkmark.svg";
import CloseIcon from "./icons/Close.svg";
import EditIcon from "./icons/Edit.svg";
import FollowSelectedIcon from "./icons/FollowSelected.svg";
import FollowUnselectedIcon from "./icons/FollowUnselected.svg";
import MembershipIcon from "./icons/Membership.svg";
import AccountIcon from "./icons/Account.svg";
import MenuIcon from "./icons/Menu.svg";
import NewsmixIcon from "./icons/Newsmix.svg";
import PushIcon from "./icons/PushNotifications.svg";
import RefreshIcon from "./icons/Refresh.svg";
import SearchIcon from "./icons/Search.svg";
import SettingsIcon from "./icons/Settings.svg";
import ShareIcon from "./icons/Share.svg";
import SignInIcon from "./icons/Signin.svg";
import SignOutIcon from "./icons/Signout.svg";
import BackIcon from "./icons/Back.svg";
import Checkbox from "./icons/Checkbox.svg";
import CheckboxSelected from "./icons/CheckboxSelected.svg";
import NewsletterIcon from "./icons/Newsletter.svg";
import WikipediaIcon from "./icons/Wikipedia.svg";
import DotsIcon from "./icons/More.svg";
import styles from "./Icon.module.scss";

type SvgIcon = React.ComponentType<React.SVGProps<SVGSVGElement>>;

const ICON_MAP: Record<string, SvgIcon> = {
  add: AddIcon,
  account: AccountIcon,
  appearance: AppearanceIcon,
  arrowLeft: ArrowLeftIcon,
  arrowRight: ArrowRightIcon,
  bookmarkSelected: BookmarkSelectedIcon,
  bookmarkUnselected: BookmarkUnselectedIcon,
  caretLeft: CaretLeftIcon,
  caretRight: CaretRightIcon,
  caretUp: CaretUpIcon,
  caretDown: CaretDownIcon,
  checkbox: Checkbox,
  checkboxSelected: CheckboxSelected,
  checkmark: CheckmarkIcon,
  close: CloseIcon,
  dots: DotsIcon,
  edit: EditIcon,
  followSelected: FollowSelectedIcon,
  followUnselected: FollowUnselectedIcon,
  membership: MembershipIcon,
  menu: MenuIcon,
  newsmix: NewsmixIcon,
  share: ShareIcon,
  push: PushIcon,
  refresh: RefreshIcon,
  search: SearchIcon,
  settings: SettingsIcon,
  signin: SignInIcon,
  signout: SignOutIcon,
  back: BackIcon,
  newsletter: NewsletterIcon,
  wikipedia: WikipediaIcon,
} as const;

type IconVariant = keyof typeof ICON_MAP;

interface IconProps {
  variant: IconVariant;
  size?: "small" | "normal" | "medium" | "large" | "dynamic";
  className?: string;
  ariaLabel?: string;
  inPrimaryButtonContext?: boolean;
  isNative?: boolean;
}

const Icon = ({
  variant,
  size = "normal",
  className,
  inPrimaryButtonContext,
  ariaLabel,
  isNative,
}: IconProps) => {
  const IconElement = ICON_MAP[variant];

  if (!IconElement) return null;

  // We assume that the icon is purely decorative
  // if no ariaLabel is passed
  const accessibilityProps = ariaLabel
    ? { role: "img", "aria-label": ariaLabel }
    : { "aria-hidden": "true" as const };

  return (
    <i
      className={cn([
        styles.icon,
        styles[size],
        inPrimaryButtonContext && styles.inPrimaryButton,
        className ? className : styles.iconColor,
        isNative && styles.native,
      ])}
    >
      <IconElement
        {...accessibilityProps}
        focusable="false"
        data-icon-variant={variant}
      />
    </i>
  );
};

export { Icon, ICON_MAP };
export type { IconVariant };
